/**
 * AP-32 13.4 — **Belegarten einrichten:** aktiv, Präfix, Stellen, Jahresreihe,
 * Nummernmuster, Startwert, Layout, Preise, Projektpflicht, Fälligkeit.
 *
 * Sobald in einer Reihe eine Nummer vergeben ist, steht die Jahresreihe fest
 * (6.4) — das Feld ist dann **erklärt gesperrt**, nicht versteckt.
 *
 * AP-36 Phase D: ohne Knopf. Jede Zeile ist die Stelle ihrer Art
 * (`documentKind:<art>`, `lib/belegart-stelle.ts`), jedes Feld speichert beim
 * Verlassen bzw. sofort.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowLeft, Lock } from 'lucide-react'
import { formatiereBelegnummer } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { FeldStatus, FeldStatusZeichen, useFeld, useStellenDaten, type Datensatz } from '@/lib/speichern'
import { documentsApi } from '../api/documents.api'
import { SettingsTabs } from '../components/SettingsTabs'
import { KindOptionsEditor } from '../components/KindOptionsEditor'
import { useBelegartStelle } from '../lib/belegart-stelle'
import type { DocumentSettingsData, KindSetting } from '../types/document.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 aria-[invalid=true]:border-destructive'

export default function DocumentSettingsPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [daten, setDaten] = useState<DocumentSettingsData | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    documentsApi
      .settings()
      .then((r) => setDaten(r.data.data))
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [t])

  if (!daten) {
    return <div className={pageWide}>{fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : t('common.loading')}</div>
  }

  return (
    <div className={pageWide}>
      <div className="flex items-center gap-3">
        <Link to="/correspondence" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('documents.settings.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('documents.settings.subtitle')}</p>
        </div>
      </div>
      <SettingsTabs />

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {/* Nachtrag 2: Die Platzhalter stehen oben — wie bei den Textbausteinen. */}
      <p className="text-xs text-muted-foreground">{t('documents.settings.patternHelp')}</p>

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t('documents.settings.kind')}</th>
              <th className="px-3 py-2">{t('documents.settings.enabled')}</th>
              <th className="px-3 py-2" title={t('documents.settings.requirePostingHint')}>{t('documents.settings.requirePosting')}</th>
              <th className="px-3 py-2">{t('documents.settings.prefix')}</th>
              <th className="px-3 py-2">{t('documents.settings.pattern')}</th>
              <th className="px-3 py-2">{t('documents.settings.digits')}</th>
              <th className="px-3 py-2">{t('documents.settings.yearly')}</th>
              <th className="px-3 py-2">{t('documents.settings.nextValue')}</th>
              <th className="px-3 py-2">{t('documents.settings.layout')}</th>
              <th className="px-3 py-2">{t('documents.settings.showPrices')}</th>
              <th className="px-3 py-2">{t('documents.settings.requireProject', { project: terms.project })}</th>
              <th className="px-3 py-2">{t('documents.settings.dueDays')}</th>
            </tr>
          </thead>
          <tbody>
            {daten.kinds.map((k, i) => (
              <BelegartZeile key={k.kind} art={k} layouts={daten.layouts} gerade={i % 2 === 0} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t('documents.settings.requirePostingHint')}</p>
      <KindOptionsEditor daten={daten} />
    </div>
  )
}

/** Eine Belegart — eine Zeile, eine Stelle. */
function BelegartZeile({ art, layouts, gerade }: { art: KindSetting; layouts: DocumentSettingsData['layouts']; gerade: boolean }) {
  const { t } = useTranslation()
  const stelle = useBelegartStelle(art.kind, art as unknown as Datensatz, t(`documents.kinds.${art.kind}`))
  const d = useStellenDaten(stelle) as unknown as KindSetting
  const gesperrt = d.numberRangeLocked ?? art.numberRangeLocked
  const ganz = (min: number, max: number) => (w: number | null) =>
    w !== null && (!Number.isInteger(w) || w < min || w > max) ? `${min} bis ${max}` : null

  const aktiv = useFeld<boolean>(stelle, 'isEnabled', { art: 'schalter', label: t('documents.settings.enabled') })
  const buchen = useFeld<boolean>(stelle, 'requirePosting', { art: 'schalter', label: t('documents.settings.requirePosting') })
  const praefix = useFeld<string>(stelle, 'prefix', { label: t('documents.settings.prefix'), lies: (x) => x.trim() })
  const muster = useFeld(stelle, 'numberPattern', { label: t('documents.settings.pattern'), pflicht: true })
  const stellen = useFeld<number | null>(stelle, 'digits', { art: 'zahl', label: t('documents.settings.digits'), pflicht: true, pruefe: ganz(1, 9) })
  const jahr = useFeld<boolean>(stelle, 'yearlyReset', { art: 'schalter', label: t('documents.settings.yearly'), gesperrt })
  const naechste = useFeld<number | null>(stelle, 'nextValue', {
    art: 'zahl',
    label: t('documents.settings.nextValue'),
    pflicht: true,
    // Der Zähler läuft nur vorwärts, sobald eine Nummer vergeben ist (N-8).
    pruefe: ganz(gesperrt ? art.nextValue : 1, 999_999_999),
  })
  const layout = useFeld(stelle, 'layoutId', { art: 'auswahl', label: t('documents.settings.layout') })
  const preise = useFeld<boolean>(stelle, 'showPrices', { art: 'schalter', label: t('documents.settings.showPrices') })
  const projekt = useFeld<boolean>(stelle, 'requireProject', { art: 'schalter', label: t('documents.settings.requireProject') })
  const faellig = useFeld<number | null>(stelle, 'defaultDueDays', { art: 'zahl', label: t('documents.settings.dueDays'), pruefe: ganz(0, 365) })

  const vorschau = formatiereBelegnummer(muster.wert ?? '', {
    prefix: praefix.wert ?? '',
    jahr: jahr.wert ? new Date().getFullYear() : 0,
    monat: new Date().getMonth() + 1,
    laufnummer: Math.max(1, naechste.wert ?? 1),
    stellen: Math.min(9, Math.max(1, stellen.wert || 1)),
  })
  const alle = [aktiv, buchen, praefix, muster, stellen, jahr, naechste, layout, preise, projekt, faellig]
  const fehlerText = alle.map((f) => f.fehler).find(Boolean)

  const haken = (f: typeof aktiv) => (
    <span className="inline-flex items-center gap-1">
      <input type="checkbox" {...f.schalter} />
      <FeldStatusZeichen status={f} />
    </span>
  )

  return (
    <>
      <tr className={`border-b border-border last:border-0 ${gerade ? 'bg-background' : 'bg-muted/20'}`}>
        <td className="whitespace-nowrap px-3 py-2 font-medium">
          {t(`documents.kinds.${art.kind}`)}
          {gesperrt && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground" title={t('documents.settings.locked')}>
              <Lock className="h-3 w-3" />
              {t('documents.settings.issued', { count: d.postedCount ?? art.postedCount })}
            </span>
          )}
        </td>
        <td className="px-3 py-2">{haken(aktiv)}</td>
        <td className="px-3 py-2">
          {/* N-10: nur, wo die Art es zulässt — Rechnungsarten, Gutschrift, Storno, Mahnung bleiben fix. */}
          {art.capabilities.postingOptional ? (
            haken(buchen)
          ) : (
            <span className="text-xs text-muted-foreground">{t('documents.settings.requirePostingFixed')}</span>
          )}
        </td>
        <td className="px-3 py-2">
          <input className={`${eingabe} w-20`} maxLength={12} {...praefix.props} />
        </td>
        <td className="px-3 py-2">
          {/* N-8: Muster, Präfix und Stellen bleiben änderbar — der Zähler läuft nur vorwärts. */}
          <input className={`${eingabe} w-48 font-mono`} {...muster.props} />
          <div className="mt-0.5 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
            {t('documents.settings.patternPreview', { number: vorschau })}
          </div>
        </td>
        <td className="px-3 py-2">
          <input inputMode="numeric" className={`${eingabe} w-16`} {...stellen.props} />
        </td>
        <td className="px-3 py-2">{haken(jahr)}</td>
        <td className="px-3 py-2">
          <input inputMode="numeric" className={`${eingabe} w-24`} {...naechste.props} />
        </td>
        <td className="px-3 py-2">
          <select className={`${eingabe} w-40`} {...layout.props}>
            <option value="">{t('documents.settings.layoutDefault')}</option>
            {layouts.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2">
          {art.capabilities.prices === 'optional' ? (
            haken(preise)
          ) : (
            <span className="text-xs text-muted-foreground">{t(art.capabilities.prices === 'yes' ? 'documents.settings.pricesAlways' : 'documents.settings.pricesNever')}</span>
          )}
        </td>
        <td className="px-3 py-2">{haken(projekt)}</td>
        <td className="px-3 py-2">
          {art.capabilities.payable ? (
            <input inputMode="numeric" className={`${eingabe} w-20`} {...faellig.props} />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {fehlerText && (
        <tr className={gerade ? 'bg-background' : 'bg-muted/20'}>
          <td colSpan={12} className="px-3 pb-2">
            <FeldStatus status={alle.find((f) => f.fehler)!} zeichen={false} />
          </td>
        </tr>
      )}
    </>
  )
}
